import { useEffect, useLayoutEffect, useRef } from "react";
import { useCall } from "@/call/CallProvider";
import { NOTES } from "@/call/notes";
import { InkedPhone } from "./ink";
import { ReceptionistOrb } from "./Orb";

/**
 * A call, drawn: her, what she is doing, what has been said, a line if
 * something needs saying, and the button.
 *
 * Two of these mount — one on the page under the hero, one in the floating
 * panel — and both render the same state, so they can never disagree about
 * what the call is doing.
 */
export function CallView({ view }: { view: "page" | "float" }) {
  const { state, press } = useCall();
  const onPage = view === "page";

  // At rest until a call is pressed, and back at rest once nothing from the
  // call is left on screen.
  const fresh = state.phase === "ready" && !state.turns.length && !state.note;
  const look = state.phase === "live" ? state.mode : fresh && onPage ? "rest" : "idle";

  const label =
    state.phase === "connecting"
      ? "Connecting…"
      : state.phase === "live"
        ? "End call"
        : "Call it live";

  const note = state.note ? NOTES[state.note] : null;

  return (
    <div className="cv" data-view={view} data-phase={state.phase} data-look={look}>
      {/* Her orb, the same size throughout and in the same place: the room
          the transcript needs opens under her and the panel grows to hold it.
          It breathes while she listens and swells while she talks. */}
      <div className="stage">
        <ReceptionistOrb className="her" />
      </div>

      {/* The dots keep moving while she does. */}
      <p className="status">
        <span>{state.mode === "talking" ? "Talking" : "Listening"}</span>
        <b className="ell" aria-hidden="true">
          <i>.</i>
          <i>.</i>
          <i>.</i>
        </b>
      </p>

      <Transcript turns={state.turns} />

      {/* The band is always there; only the line inside it comes and goes. */}
      <p className={`note${note ? " on" : ""}`} role="status">
        <span className={note?.kind}>{note?.node}</span>
      </p>

      <button
        className={`call${state.phase === "ready" ? " ring" : ""}`}
        type="button"
        disabled={state.phase === "limit"}
        onClick={press}
      >
        <InkedPhone />
        <span>{label}</span>
      </button>
    </div>
  );
}

/**
 * Newest at the bottom, and what has scrolled off the top fades out. It
 * follows her as she speaks, unless the visitor has
 * scrolled back to read something — then it stays where they left it until
 * they come back to the bottom.
 *
 * Nothing labels who said what: hers sit left and white, the caller's right and
 * grey, which is how every phone shows a conversation.
 */
function Transcript({ turns }: { turns: { who: "her" | "you"; text: string }[] }) {
  const log = useRef<HTMLDivElement>(null);
  const follow = useRef(true);

  useLayoutEffect(() => {
    const el = log.current;
    if (el && follow.current) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // The floating panel is rendered hidden until it is opened, so its transcript
  // is first laid out with no height at all and the line it scrolled to the
  // bottom of was zero pixels tall. It is measured again when it has a size.
  useEffect(() => {
    const el = log.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (follow.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    // The transcript grows in place, so a screen reader hears only what is new.
    <div
      className="log"
      role="log"
      ref={log}
      onScroll={(e) => {
        const el = e.currentTarget;
        follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
      }}
    >
      {turns.map((t, i) => (
        <p className={`turn ${t.who}`} key={i}>
          {t.text}
        </p>
      ))}
    </div>
  );
}
